import { keepPreviousData } from "@tanstack/react-query";
import noop from "lodash/noop";
import { Chat } from "PFApp/components/chat/chat";
import { ChatState } from "PFApp/components/chat/chat_states";
import { usePusherEvent } from "PFCore/base/pusher";
import { useChatConversation } from "PFCore/hooks/queries/chat/use_chat_conversation";
import { useChatInvalidate } from "PFCore/hooks/queries/chat/use_chat_invalidate";
import { useChatMessages } from "PFCore/hooks/queries/chat/use_chat_messages";
import { useChatParticipants } from "PFCore/hooks/queries/chat/use_chat_participants";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Conversation, Id } from "PFTypes";
import { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useClearUnreadOnFetch } from "./use_clear_unread_on_fetch";
import { useFlashTitleOnNewMessage } from "./use_flash_title_on_new_message";

type PusherChatMessageEvent = { id: Id };

type ChatControllerProps = {
  style?: CSSProperties;
  conversationId: number;
  selected?: boolean;
  expanded?: boolean;
  expandable?: boolean;
  emptyMessage?: string;
  isBelow?: boolean;
  minimized?: boolean;
  onSelect: (id: number) => void;
  onMinimize?: (id: number | string) => void;
  onExpand?: (id: number, value: boolean) => void;
  onUnMinimize?: (id: number) => void;
  onClose?: (id: number) => void;
  onClearNotifications?: () => void;
};

export const ChatController = ({
  style,
  conversationId,
  selected,
  expanded,
  expandable,
  emptyMessage,
  isBelow,
  minimized,
  onSelect = noop,
  onMinimize = noop,
  onExpand = noop,
  onUnMinimize = noop,
  onClose = noop,
  onClearNotifications = noop
}: ChatControllerProps) => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation();
  const resolvedEmptyMessage = emptyMessage ?? t("chat.noMessages");

  const [chatState, setChatState] = useState<ChatState>(ChatState.Loading);
  const [flashTitle, setFlashTitle] = useState(false);
  const [lastReceivedMessage, setLastReceivedMessage] = useState<PusherChatMessageEvent | null>(null);

  const { invalidateAllConversations } = useChatInvalidate();

  const { data: conversation, refetch: refetchConversation } = useChatConversation(conversationId, {
    gcTime: 0 // we need to refetch conversation each time chat is opened to get fresh data
  });

  const { data: participants = [], refetch: refetchChatParticipants } = useChatParticipants({
    conversationId: conversationId,
    perPage: 5
  });
  const { data: currentParticipants = [] } = useChatParticipants({
    conversationId: conversationId,
    profileId: currentProfile.id,
    perPage: 1
  });

  const {
    data: messages,
    isFetching,
    isFetched,
    isFetchingNextPage: isFetchingPreviousMessages,
    refetch: refetchMessages,
    fetchNextPage
  } = useChatMessages(
    {
      conversationId
    },
    {
      enabled: !!conversation,
      placeholderData: keepPreviousData,
      gcTime: 0 // we need to refetch messages each time chat is opened to get fresh data
    }
  );

  const allParticipants = useMemo(
    () => (currentParticipants.length ? [currentParticipants[0], ...participants] : participants),
    [currentParticipants, participants]
  );

  const unreadCount = conversation?.unreadCount ?? 0;

  useClearUnreadOnFetch({
    isFetched,
    unreadCount,
    onClearNotifications,
    invalidateAllConversations,
    refetchConversation,
    setChatState
  });

  // we do not fetch messages when chat is minimized,
  // when chat is unminimized we need to refresh messages and conversation
  const handleUnMinimize = useCallback(async () => {
    onUnMinimize?.(conversation!.id);
    if (unreadCount > 0) {
      await refetchMessages();
      await refetchConversation();
      onClearNotifications();
      invalidateAllConversations();
      setChatState(ChatState.Normal);
    }
  }, [
    conversation,
    onUnMinimize,
    unreadCount,
    refetchMessages,
    refetchConversation,
    onClearNotifications,
    invalidateAllConversations
  ]);

  useEffect(() => {
    setChatState(isFetching ? ChatState.Loading : ChatState.Normal);
  }, [isFetching]);

  const handlePusherNewMessage = (args: PusherChatMessageEvent) => {
    setLastReceivedMessage(args);
    // On activity page we do not show newly created conversations (without any message)
    // We need to refresh data when first message is received
    if (conversation!.target && conversation!.createdAt === conversation!.lastActivityAt) {
      invalidateAllConversations();
      refetchConversation();
    }
  };

  usePusherEvent(`private-conversation-${conversationId}`, "message", handlePusherNewMessage);
  usePusherEvent(`private-conversation-${conversationId}`, "change", refetchChatParticipants);

  useFlashTitleOnNewMessage({
    lastReceivedMessage,
    messages,
    minimized,
    refetchConversation,
    refetchMessages,
    setFlashTitle,
    setLastReceivedMessage
  });

  const handleLoadPreviousMessages = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return (
    <Chat
      style={style}
      selected={!!selected}
      expanded={!!expanded}
      expandable={!!expandable}
      isBelow={isBelow}
      minimized={!!minimized}
      emptyMessage={resolvedEmptyMessage}
      onClose={onClose}
      onExpand={onExpand}
      onLoadPreviousMessages={handleLoadPreviousMessages}
      onMinimize={onMinimize}
      onSelect={onSelect}
      onUnMinimize={handleUnMinimize}
      participants={allParticipants}
      messages={messages}
      conversation={(conversation || {}) as Conversation}
      chatState={chatState}
      setChatState={setChatState}
      currentProfile={currentProfile}
      flashTitle={flashTitle}
      isFetchingPreviousMessages={!!isFetchingPreviousMessages}
    />
  );
};
